import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiBookReadLine, RiDownloadLine, RiSearchLine, RiArrowRightUpLine, RiLoader4Line } from 'react-icons/ri';
import Footer from '../components/Footer';
import { API_BASE_URL, ASSET_URL } from '../config';

const Publications = () => {
    const [categories, setCategories] = useState(["All"]);
    const [activeTab, setActiveTab] = useState("All");
    const [publications, setPublications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Categories
                const catRes = await fetch(`${API_BASE_URL}/archive/get_categories.php`);
                const catData = await catRes.json();
                if (catRes.ok) {
                    setCategories(["All", ...(catData.records || [])]);
                }

                // Fetch Publications
                const pubRes = await fetch(`${API_BASE_URL}/archive/read.php`);
                const pubData = await pubRes.json();
                if (pubRes.ok) {
                    setPublications(pubData.records || []);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredPubs = (activeTab === "All"
        ? publications
        : publications.filter(p => p.category === activeTab))
        .filter(p =>
            p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.doi && p.doi.toLowerCase().includes(searchTerm.toLowerCase()))
        );

    return (
        <div className="bg-white pt-32 min-h-screen">
            <div className="container mx-auto px-6 max-w-7xl pb-20">
                {/* Header Sub-section */}
                <div className="max-w-3xl mb-16">
                    <motion.span
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em] mb-4 block"
                    >
                        Knowledge Center
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold text-slate-900 font-display mb-6"
                    >
                        Scientific <span className="text-emerald-600 italic">Archives.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-500 font-medium leading-relaxed font-sans"
                    >
                        Access our collection of peer-reviewed research, project reports, and white papers dedicated to global biodiversity conservation.
                    </motion.p>
                </div>

                {/* Filters & Search */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-16 pb-8 border-b border-slate-100">
                    <div className="flex gap-4 overflow-x-auto pb-2 w-full md:w-auto scrollbar-hide">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setActiveTab(cat)}
                                className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 whitespace-nowrap ${activeTab === cat
                                    ? 'bg-slate-900 text-white shadow-xl translate-y-[-2px]'
                                    : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full md:w-80">
                        <RiSearchLine className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search research papers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-6 py-3 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 transition-all font-sans"
                        />
                    </div>
                </div>

                {/* Content Grid */}
                <div className="min-h-[400px]">
                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <RiLoader4Line className="animate-spin text-emerald-500 text-4xl" />
                        </div>
                    ) : filteredPubs.length === 0 ? (
                        <div className="text-center py-20 text-slate-400 font-medium">
                            No match found for your search criteria.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <AnimatePresence mode="popLayout">
                                {filteredPubs.map((pub, i) => (
                                    <motion.div
                                        key={pub.id}
                                        layout
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.4 }}
                                        onClick={() => {
                                            const targetUrl = pub.doi ? pub.doi : (pub.file_url?.startsWith('http') ? pub.file_url : (pub.file_url ? `${ASSET_URL}${pub.file_url}` : null));
                                            if (targetUrl) window.open(targetUrl, '_blank');
                                        }}
                                        className={`group relative bg-white p-8 rounded-[32px] border border-slate-100 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/5 ${(pub.file_url || pub.doi) ? 'cursor-pointer' : ''}`}
                                    >
                                        <div className="flex items-start justify-between mb-8">
                                            <div className="p-4 bg-emerald-50 rounded-2xl text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-500">
                                                <RiBookReadLine size={24} />
                                            </div>
                                            <div className="text-right">
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest block mb-1">{pub.publication_date}</span>
                                                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded-full">{pub.category}</span>
                                            </div>
                                        </div>

                                        <h3 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-emerald-700 transition-colors leading-snug font-display">
                                            {pub.title}
                                        </h3>
                                        {pub.doi && (
                                            <div className="mb-4">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-lg">
                                                    DOI: {pub.doi.replace('https://doi.org/', '')}
                                                </span>
                                            </div>
                                        )}
                                        <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8 line-clamp-3 font-sans">
                                            {pub.description}
                                        </p>

                                        <div className="flex flex-wrap gap-2 mb-8">
                                            {pub.tags && pub.tags.map(tag => (
                                                <span key={tag} className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em]">#{tag}</span>
                                            ))}
                                        </div>

                                        <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                                            <span className="text-xs font-bold text-slate-400 italic">By {pub.author}</span>
                                            {(pub.file_url || pub.doi) && (
                                                <div className="flex items-center gap-2 text-xs font-black text-emerald-600 uppercase tracking-widest group-hover:text-emerald-700">
                                                    {pub.doi ? 'View Via DOI' : 'Open Paper'}
                                                    <RiDownloadLine className="group-hover:translate-y-0.5 transition-transform" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Floating indicator */}
                                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <RiArrowRightUpLine size={20} className="text-emerald-400" />
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Publications;
